import { TextAreaField } from "@/components/common/text-field";
import BasicRichTextEditor from "@/components/common/editor/basic-rich-text-editor";
import {
  Button,
  GroupTextField,
  GroupTextFieldRow,
  Heading,
  Icon,
  Input,
  useToast,
} from "@elaniintc/goes-design-system";
import { Controller, useForm, useFieldArray, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import ScrollModal from "@/components/common/scroll-modal";
import { DialogDescription, DialogTitle } from "@/components/ui/dialog";
import AddDocumentDivider from "./AddDocumentDivider";
import z from "zod";
import { DropdownList } from "@/components/ui/dropdown-list/dropdown-list";
import { cn } from "@/utils/theme";
import {
  useCreateProcessDetails,
  useGetProcess,
  type CreateProcessDetailsPayload,
} from "@/queries/process/process.api";
import { useParams } from "react-router";
import { formatNumberInput } from "@/utils/number";
import React from "react";

interface EditProcessModalProps {
  isOpen?: boolean;
  onClose?: () => void;
  onSuccess?: () => void;
}

const documentSchema = z.object({
  name: z.string().min(1, "El nombre del documento es requerido"),
  description: z.string().max(50, "Máximo 50 caracteres").optional(),
});

const portalDetailsSchema = z
  .object({
    description: z.string().max(10000, "Máximo 10000 caracteres").optional(),
    minCost: z.number({
      error: (issue) => {
        if (issue.input === undefined) return "El costo mínimo es requerido";
        if (typeof issue.input !== "number") return "Debe ser un número";
      },
    }),
    maxCost: z.number({
      error: (issue) => {
        if (issue.input === undefined) return "El costo máximo es requerido";
        if (typeof issue.input !== "number") return "Debe ser un número";
      },
    }),
    minEstimatedTime: z.number({
      error: (issue) => {
        if (issue.input === undefined) return "El tiempo mínimo es requerido";
        if (typeof issue.input !== "number") return "Debe ser un número";
      },
    }),
    minTimeUnit: z.string().min(1, "La unidad es requerida"),
    maxEstimatedTime: z.number({
      error: (issue) => {
        if (issue.input === undefined) return "El tiempo máximo es requerido";
        if (typeof issue.input !== "number") return "Debe ser un número";
      },
    }),
    maxTimeUnit: z.string().min(1, "La unidad es requerida"),
    minSteps: z.number({
      error: (issue) => {
        if (issue.input === undefined)
          return "Los pasos mínimos son requeridos";
        if (typeof issue.input !== "number") return "Debe ser un número";
      },
    }),
    maxSteps: z.number({
      error: (issue) => {
        if (issue.input === undefined)
          return "Los pasos máximos son requeridos";
        if (typeof issue.input !== "number") return "Debe ser un número";
      },
    }),
    documents: z.array(documentSchema).min(1, "Agrega al menos un documento"),
  })
  .refine(
    (data) => {
      return data.minCost <= data.maxCost;
    },
    {
      message: "El costo mínimo no puede ser mayor al máximo",
      path: ["minCost"],
    },
  )
  .refine(
    (data) => {
      return data.minSteps <= data.maxSteps;
    },
    {
      message: "Los pasos mínimos no pueden ser mayores a los máximos",
      path: ["minSteps"],
    },
  );

type PortalDetailsData = z.infer<typeof portalDetailsSchema>;

const units = [
  { label: "Días", value: "Días" },
  { label: "Semanas", value: "Semanas" },
  { label: "Meses", value: "Meses" },
];

const CostInput = ({
  value,
  onChange,
  onBlur,
  placeholder,
  group,
}: {
  value: number | undefined;
  onChange: (value: number | undefined) => void;
  onBlur: () => void;
  placeholder: string;
  group: "left" | "right";
}) => {
  const [displayValue, setDisplayValue] = React.useState(
    value ? Number(value).toFixed(2) : "",
  );

  React.useEffect(() => {
    if (value === undefined || value === null) {
      setDisplayValue("");
    } else {
      setDisplayValue(Number(value).toFixed(2));
    }
  }, [value]);

  return (
    <Input
      group={group}
      className="w-full font-normal pl-9"
      placeholder={placeholder}
      value={displayValue}
      onChange={(e) => {
        const formattedValue = formatNumberInput(
          e.target.value,
          true,
          false,
          2,
        );

        setDisplayValue(formattedValue);

        if (formattedValue === "") {
          onChange(undefined);
          return;
        }

        const num = parseFloat(formattedValue);
        if (!isNaN(num)) {
          onChange(num);
        }
      }}
      onBlur={() => {
        onBlur();
        // Al salir del campo, mantener el formato
        if (value) {
          const formatted = Number(value).toFixed(2);
          setDisplayValue(formatted);
        }
      }}
    />
  );
};

export const PortalDetailsModal = ({
  isOpen = false,
  onClose = () => {},
}: EditProcessModalProps) => {
  const { showToast } = useToast();
  const { id } = useParams();
  const { data: process } = useGetProcess(id);
  const { mutate: createProcessDetails, isPending } =
    useCreateProcessDetails(id);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<PortalDetailsData>({
    resolver: zodResolver(portalDetailsSchema),
    defaultValues: {
      description: "",
      minCost: undefined,
      maxCost: undefined,
      minEstimatedTime: undefined,
      minTimeUnit: "Días",
      maxEstimatedTime: undefined,
      maxTimeUnit: "Días",
      minSteps: undefined,
      maxSteps: undefined,
      documents: [{ name: "", description: "" }],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "documents",
  });

  const allDocuments = useWatch({
    control,
    name: "documents",
  });

  const description = useWatch({
    control,
    name: "description",
  });

  React.useEffect(() => {
    if (process?.data) {
      const {
        portal_description,
        estimated_cost_min,
        estimated_cost_max,
        estimated_time_min,
        estimated_time_max,
        estimated_time_unit,
        estimated_steps_min,
        estimated_steps_max,
        required_documents,
      } = process.data;

      reset({
        description: portal_description || "",
        minCost: estimated_cost_min ? Number(estimated_cost_min) : undefined,
        maxCost: estimated_cost_max ? Number(estimated_cost_max) : undefined,
        minEstimatedTime: estimated_time_min
          ? Number(estimated_time_min)
          : undefined,
        maxEstimatedTime: estimated_time_max
          ? Number(estimated_time_max)
          : undefined,
        minTimeUnit: estimated_time_unit || "Días",
        maxTimeUnit: estimated_time_unit || "Días",
        minSteps: estimated_steps_min ? Number(estimated_steps_min) : undefined,
        maxSteps: estimated_steps_max ? Number(estimated_steps_max) : undefined,
        documents: required_documents?.length
          ? required_documents.map((doc) => ({
              name: doc.name,
              description: doc.description || "",
            }))
          : [{ name: "", description: "" }],
      });
    }
  }, [process, reset]);

  const transformFormDataToPayload = (
    data: PortalDetailsData,
  ): CreateProcessDetailsPayload => {
    return {
      portal_description: data.description || undefined,
      status: process?.data.status ?? "INACTIVE",
      estimated_cost_min: parseFloat(data.minCost.toFixed(2)),
      estimated_cost_max: parseFloat(data.maxCost.toFixed(2)),
      estimated_time_min: data.minEstimatedTime,
      estimated_time_max: data.maxEstimatedTime,
      estimated_time_unit: data.minTimeUnit,
      estimated_steps_min: data.minSteps,
      estimated_steps_max: data.maxSteps,
      required_documents: data.documents.map((doc) => ({
        name: doc.name,
        description: doc.description || undefined,
      })),
    };
  };

  const handleSave = async (data: PortalDetailsData) => {
    const payload = transformFormDataToPayload(data);
    createProcessDetails(payload, {
      onSuccess: () => {
        showToast({
          title: "Proceso creado",
          description: "El proceso se creó correctamente.",
          options: { type: "success", duration: 4000 },
        });
        onClose();
      },
      onError: () => {
        showToast({
          title: "Error",
          description: "No se pudo crear el proceso.",
          options: { type: "error", duration: 4000 },
        });
      },
    });
  };

  return (
    <ScrollModal
      isOpen={isOpen}
      onClose={onClose}
      modalContentClassName="w-full max-w-[520px] max-h-[680px] h-full flex flex-col gap-0"
      header={
        <div className="flex items-center justify-between px-8 py-3">
          <DialogTitle>Detalles en portal</DialogTitle>
          <DialogDescription className="sr-only">
            Formulario para editar un proceso en portal
          </DialogDescription>
          <Button
            type="button"
            onClick={onClose}
            variant="quiet"
            aria-label="Cerrar"
            className="w-8 h-8 cursor-pointer"
          >
            <Icon type="far" name="times" />
          </Button>
        </div>
      }
      footer={
        <div className="grid grid-cols-2 gap-2 py-3 px-8">
          <Button
            type="button"
            variant="tertiary"
            className="w-full font-semibold border"
            onClick={onClose}
            disabled={isPending}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            form="edit-process-form"
            className="w-full font-semibold"
            disabled={isPending}
          >
            Guardar cambios
          </Button>
        </div>
      }
    >
      <div className="p-modal-md flex flex-col gap-3">
        <form
          className="flex flex-col flex-1 min-h-0 gap-6"
          onSubmit={handleSubmit(handleSave)}
          id="edit-process-form"
        >
          <Heading variant="h6">Detalles generales</Heading>
          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <div className="flex flex-col gap-1">
                <BasicRichTextEditor
                  value={field.value || ""}
                  onChange={field.onChange}
                  label="Descripción de trámite"
                  labelClassName="text-base"
                  placeholder="Escribe la descripción..."
                  minHeight="120px"
                  showAlignment
                  error={errors.description?.message || ""}
                />
                <div className="flex justify-end text-xs leading-xl font-normal px-1">
                  <span
                    className={cn(
                      "text-body-quiet",
                      (description?.length || 0) >= 10000 && "text-danger",
                    )}
                  >
                    Caracteres:
                  </span>
                  <span
                    className={cn(
                      "font-geist-mono ml-1",
                      (description?.length || 0) >= 10000
                        ? "text-danger"
                        : "text-body",
                    )}
                  >
                    {description?.length || 0}/10000
                  </span>
                </div>
              </div>
            )}
          />

          <GroupTextField
            label="Costo aproximado"
            required
            className="w-full"
            error={errors.minCost?.message || errors.maxCost?.message || ""}
          >
            <div className="flex w-full">
              <div className="flex-1 relative">
                <Icon
                  type="far"
                  name="dollar-sign"
                  className="absolute left-2 top-1/2 transform -translate-y-1/2 text-body text-sm leading-2xl z-10"
                />
                <Controller
                  name="minCost"
                  control={control}
                  render={({ field }) => (
                    <CostInput
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      placeholder="Costo mínimo"
                      group="left"
                    />
                  )}
                />
              </div>
              <div className="flex-1 relative">
                <Icon
                  type="far"
                  name="dollar-sign"
                  className="absolute left-2 top-1/2 transform -translate-y-1/2 text-body text-sm leading-2xl z-10"
                />
                <Controller
                  name="maxCost"
                  control={control}
                  render={({ field }) => (
                    <CostInput
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      placeholder="Costo máximo"
                      group="right"
                    />
                  )}
                />
              </div>
            </div>
          </GroupTextField>

          <div>
            <div className="flex items-end w-full gap-2">
              <GroupTextField
                label="Tiempo estimado"
                required
                className="flex-1"
              >
                <div className="flex w-full">
                  <div className="flex-[7]">
                    <Controller
                      name="minEstimatedTime"
                      control={control}
                      render={({ field }) => (
                        <Input
                          placeholder="Mínimo"
                          type="text"
                          group="left"
                          value={field.value === undefined ? "" : field.value}
                          onChange={(e) => {
                            const value = e.target.value.replace(/[^0-9]/g, "");
                            const num =
                              value === "" ? undefined : Number(value);
                            field.onChange(num);
                          }}
                          maxLength={15}
                          className="w-full py-[10.5px]"
                        />
                      )}
                    />
                  </div>
                  <div className="flex-[3]">
                    <Controller
                      name="minTimeUnit"
                      control={control}
                      render={({ field }) => (
                        <DropdownList
                          widthClassName="w-full"
                          placeholder="Unidad"
                          group="right"
                          options={units}
                          selectedValue={
                            units.find((u) => u.value === field.value) ||
                            units[0]
                          }
                          buttonVariant="quiet"
                          toggleSelection={(option) =>
                            field.onChange(option.value)
                          }
                        />
                      )}
                    />
                  </div>
                </div>
              </GroupTextField>

              <GroupTextField label="" className="flex-1">
                <div className="flex w-full">
                  <div className="flex-[7]">
                    <Controller
                      name="maxEstimatedTime"
                      control={control}
                      render={({ field }) => (
                        <Input
                          placeholder="Máximo"
                          type="text"
                          group="left"
                          value={field.value === undefined ? "" : field.value}
                          onChange={(e) => {
                            const value = e.target.value.replace(/[^0-9]/g, "");
                            const num =
                              value === "" ? undefined : Number(value);
                            field.onChange(num);
                          }}
                          maxLength={15}
                          className="w-full py-[10.5px]"
                        />
                      )}
                    />
                  </div>
                  <div className="flex-[3]">
                    <Controller
                      name="maxTimeUnit"
                      control={control}
                      render={({ field }) => (
                        <DropdownList
                          widthClassName="w-full"
                          placeholder="Unidad"
                          group="right"
                          options={units}
                          selectedValue={
                            units.find((u) => u.value === field.value) ||
                            units[0]
                          }
                          buttonVariant="quiet"
                          toggleSelection={(option) =>
                            field.onChange(option.value)
                          }
                        />
                      )}
                    />
                  </div>
                </div>
              </GroupTextField>
            </div>
            {(errors.maxEstimatedTime?.message ||
              errors.maxTimeUnit?.message) && (
              <p className="text-danger text-xs leading-xl font-normal">
                <Icon type="far" name="circle-xmark" className="mr-1" />
                {errors.maxEstimatedTime?.message ||
                  errors.maxTimeUnit?.message}
              </p>
            )}
          </div>

          <GroupTextField
            label="Número de pasos"
            required
            error={errors.minSteps?.message || errors.maxSteps?.message || ""}
          >
            <Controller
              name="minSteps"
              control={control}
              render={({ field }) => (
                <Input
                  className="w-full font-normal flex-auto"
                  placeholder="Pasos mínimos"
                  type="text"
                  group="left"
                  value={field.value === undefined ? "" : field.value}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9]/g, "");
                    const num = value === "" ? undefined : Number(value);
                    field.onChange(num);
                  }}
                />
              )}
            />
            <Controller
              name="maxSteps"
              control={control}
              render={({ field }) => (
                <Input
                  className="w-full font-normal flex-auto"
                  placeholder="Pasos máximos"
                  type="text"
                  group="right"
                  value={field.value === undefined ? "" : field.value}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9]/g, "");
                    const num = value === "" ? undefined : Number(value);
                    field.onChange(num);
                  }}
                />
              )}
            />
          </GroupTextField>

          <div className="flex-1 h-px border-neutral-quiet border" />

          <Heading variant="h6">Documentos</Heading>
          {errors.documents && !Array.isArray(errors.documents) && (
            <p className="text-danger text-sm">
              {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
              {(errors.documents as any).message}
            </p>
          )}

          {fields.map((field, index) => {
            const description = allDocuments?.[index]?.description;

            return (
              <div key={field.id} className="flex flex-col gap-1">
                <GroupTextField
                  label={index === 0 ? "Documento" : `Documento ${index + 1}`}
                  error={errors.documents?.[index]?.name?.message || ""}
                >
                  <GroupTextFieldRow>
                    <Controller
                      name={
                        `documents.${index}.name` as `documents.${number}.name`
                      }
                      control={control}
                      render={({ field }) => (
                        <Input
                          {...field}
                          className="w-full rounded-b-none"
                          placeholder="Nombre del documento"
                        />
                      )}
                    />
                  </GroupTextFieldRow>
                  <GroupTextFieldRow className="min-h-28">
                    <Controller
                      name={
                        `documents.${index}.description` as `documents.${number}.description`
                      }
                      control={control}
                      render={({ field }) => (
                        <TextAreaField
                          integrated
                          {...field}
                          label=""
                          placeholder="Descripción del documento..."
                          rows={10}
                          limit={5000}
                          disabled={!allDocuments?.[index]?.name}
                          count={description?.length || 0}
                          countAlign="right"
                        />
                      )}
                    />
                  </GroupTextFieldRow>
                </GroupTextField>

                {/* Contador FUERA del GroupTextField */}
                <div className="flex justify-end text-xs leading-xl font-normal px-1">
                  <span
                    className={cn(
                      "text-body-quiet",
                      (description?.length || 0) >= 5000 && "text-danger",
                    )}
                  >
                    Caracteres:
                  </span>
                  <span
                    className={cn(
                      "font-geist-mono ml-1",
                      (description?.length || 0) >= 5000
                        ? "text-danger"
                        : "text-body",
                    )}
                  >
                    {description?.length || 0}/5000
                  </span>
                </div>

                {fields.length > 1 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => remove(index)}
                    className="text-danger"
                  >
                    <Icon type="far" name="trash" />
                    Eliminar documento
                  </Button>
                )}
              </div>
            );
          })}

          <AddDocumentDivider
            onClick={() => append({ name: "", description: "" })}
          />
        </form>
      </div>
    </ScrollModal>
  );
};
